import type { KyResponse, Options } from "ky";

import { fetchRefreshToken } from "#src/api/user";
import { useAuthStore } from "#src/store/auth";
import ky from "ky";

import { AUTH_HEADER } from "./constants";
import { goLogin } from "./go-login";

let isRefreshing = false;

/**
 * @en Refreshes the token and retries the request
 *
 * @param request The request object
 * @param options The request options
 * @param refreshToken The refresh token
 * @returns The response object
 * @throws An exception is thrown if the token refresh fails
 */
export async function refreshTokenAndRetry(request: Request, options: Options, refreshToken: string) {
	if (!isRefreshing) {
		isRefreshing = true;
		try {
			// @en Call the fetchRefreshToken function, using the provided refreshToken to get a new token and refreshToken
			const freshResponse = await fetchRefreshToken({ refreshToken });
			// @en Extract the new token from the response
			const newToken = freshResponse.data.token;
			// @en Extract the new refreshToken from the response
			const newRefreshToken = freshResponse.data.refreshToken;
			// @en Save the new token and refreshToken to the authStore
			useAuthStore.setState({ token: newToken, refreshToken: newRefreshToken });
			// @en Call the onRefreshed function, passing the new token
			onRefreshed(newToken);

			// @en Set the request's Authorization header with the new token
			// @en Retry the current request
			request.headers.set(AUTH_HEADER, `Bearer ${newToken}`);
			// @en Re-initiate the request using the new token
			return ky(request, options);
		} catch (error) {
			// @en Call the onRefreshFailed function, passing the error object
			onRefreshFailed(error);
			// @en The refresh token authentication failed. Redirect to the login page.
			goLogin();
			throw error;
		} finally {
			isRefreshing = false;
		}
	} else {
		// @en If the token is already refreshing, the request needs to wait until the token refresh is complete
		return new Promise<KyResponse>((resolve, reject) => {
			// @en Add the refresh subscriber
			addRefreshSubscriber({
				// @en When the token refresh succeeds, set the new token in the request's Authorization header and re-initiate the request
				resolve: async (newToken) => {
					request.headers.set(AUTH_HEADER, `Bearer ${newToken}`);
					resolve(ky(request, options));
				},
				// @en When the token refresh fails, reject the current Promise
				reject,
			});
		});
	}
}

// @en Defines an array to store all subscribers waiting for the token refresh
// @en Each subscriber object contains resolve and reject methods, which are called when the token refresh succeeds or fails, respectively.
let refreshSubscribers: Array<{
	resolve: (token: string) => void // @en Function called when the token refresh succeeds, passes the new token
	reject: (error: any) => void // @en Function called when the token refresh fails, passes the error information
}> = [];

/**
 * @en Notifies all waiting subscribers when the token refresh succeeds.
 * @en Iterates through all subscribers, calls their resolve method, and passes the new token.
 * @en Then, clears the subscriber list, preparing for the next token refresh.
 *
 * @param token The refreshed token string
 */
function onRefreshed(token: string) {
	refreshSubscribers.forEach(subscriber => subscriber.resolve(token));
	refreshSubscribers = []; // @en Clear the subscriber list
}

/**
 * @en Notifies all waiting subscribers when the token refresh fails.
 * @en Iterates through all subscribers, calls their reject method, and passes the error information.
 * @en Then, clears the subscriber list.
 *
 * @param error The error object
 */
function onRefreshFailed(error: any) {
	refreshSubscribers.forEach(subscriber => subscriber.reject(error));
	refreshSubscribers = []; // @en Clear the subscriber list
}

/**
 * @en Adds a subscriber waiting for the token refresh to the list.
 *
 * @param subscriber The subscriber object containing resolve and reject methods
 */
function addRefreshSubscriber(subscriber: typeof refreshSubscribers[number]) {
	refreshSubscribers.push(subscriber);
}
